import React from 'react';
import moment from 'moment-timezone';
import { isObservableArray } from 'mobx';
import { REMINDER_DELAY_TYPE, USER_ROLE } from './constants';
import pluralize from 'pluralize';
import { palette } from '@awarego-ui/components';
import currencyFormatter from 'currency-formatter';
import { validate as validateUUID } from 'uuid';
import isEqual from 'lodash/isEqual';


export function userRoleName(roleId) {
    const role = Object.keys(USER_ROLE).find((x) => USER_ROLE[x] === roleId);
    if (!role) return 'Unknown';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}


export function getRandomArbitrary(min, max) {
    return Math.random() * (max - min) + min;
}

export function arraysEqual(a, b) {
    return isEqual([...a].sort(), [...b].sort());
}

export function backOffDelay(
    baseDelay,
    counter,
    maxDelay = 240_000, // 4 min max
    factor = 1.5        // temp of increasing
) {
    const expDelay = Math.min(
        baseDelay * Math.pow(2, counter / factor),
        maxDelay
    );

    // controlled jitter
    const jitter = Math.random() * 0.3 + 0.7; // 0.7–1.0

    return Math.round(expDelay * jitter);
}

export function trainingName(name, created) {
    return name || `Training program created on ${formatDate(created)}`;
}

export function defaultZero(value) {
    return value || 0;
}

export function enforceSlash(url) {
    return url.endsWith('/') ? url : url + '/';
}

export function extractErrorMessage(error) {
    if (error === false || error === true || error == null) return;
    if (error && error.statusCode && error.data) {
        return error.data.error.description;
    }

    let responseData =
        error.response && error.response.data && error.response.data;
    if (
        responseData &&
        responseData.errors &&
        Array.isArray(responseData.errors)
    ) {
        return responseData.errors
            .map((e) => {
                if (e.messages) return e.messages.join(';');
                return e.message;
            })
            .join('\n');
    }

    return (responseData && responseData.error) || error.message;
}

export function querystring(name, url = window.location.href) {
    name = name.replace(/[[]]/g, '\\$&');

    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)', 'i');
    const results = regex.exec(url);

    if (!results) {
        return null;
    }
    if (!results[2]) {
        return '';
    }

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function guid() {
    return (
        this.randomString4() +
        this.randomString4() +
        '-' +
        this.randomString4() +
        '-' +
        this.randomString4() +
        '-' +
        this.randomString4() +
        '-' +
        this.randomString4() +
        this.randomString4() +
        this.randomString4()
    );
}

export function randomString4() {
    return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
}

export function validateEmail(email) {
    var re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line  no-useless-escape
    return re.test(String(email).toLowerCase());
}

export function formatTime(seconds, round) {
    let duration = moment.duration(seconds, 'seconds');
    if (round) {
        return Math.ceil(duration.asMinutes());
    } else {
        return moment.utc(duration.asMilliseconds()).format('mm:ss');
    }
}

export function formatDateTimeAndTimezone(date) {
    return (
        moment(date).format('dddd, MMMM Do YYYY, HH:mm') +
        moment.tz(moment.tz.guess()).format(' z')
    );
}

export function formatDate(dateString) {
    if (!dateString) {
        return '';
    }
    try {
        const dt = new Date(dateString);
        const monthNames = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];
        return (
            monthNames[dt.getMonth()] +
            ' ' +
            dt.getDate() +
            ' ' +
            dt.getFullYear()
        );
    } catch (e) {
        return '';
    }
}

export function formatTimeOfDay(time) {
    if (time !== '') {
        return time + '.00 GMT';
    }
    return time;
}

export function getWeekday(dateObject) {
    if (!dateObject) {
        return 'null';
    }
    try {
        const weekday = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
        ];
        let weekDay = weekday[dateObject.getDay()];
        return weekDay;
    } catch (e) {
        return e.description;
    }
}

export function getHours(dateObject) {
    if (!dateObject) {
        return 'null';
    }
    try {
        return (
            dateObject.getHours() +
            ':' +
            (dateObject.getMinutes() < 10 ? '0' : '') +
            dateObject.getMinutes()
        );
    } catch (e) {
        return e.description;
    }
}

export function buildUrl(url, parameters) {
    let qs = '';
    for (let key in parameters) {
        const value = parameters[key];
        qs += encodeURIComponent(key) + '=' + encodeURIComponent(value) + '&';
    }
    if (qs.length > 0) {
        qs = qs.substring(0, qs.length - 1); //chop off last "&"
        url = url + '?' + qs;
    }
    return url;
}

export function buildXtraParameters(company_id, campaign_id, subject_id) {
    let xtra_params = '';
    if (company_id && company_id > 0) {
        xtra_params += 'o=' + company_id;
    }
    if (campaign_id && campaign_id > 0) {
        if (xtra_params.length > 0) {
            xtra_params += '&';
        }
        xtra_params += 'c=' + campaign_id;
    }
    if (subject_id && subject_id > 0) {
        if (xtra_params.length > 0) {
            xtra_params += '&';
        }
        xtra_params += 's=' + subject_id;
    }
    return xtra_params;
}

export function host() {
    return (
        window.location.protocol +
        '//' +
        window.location.hostname +
        (window.location.port ? ':' + window.location.port : '')
    );
}

export function alert(message) {
    console.info(message);
}

export function t(store, key) {
    const i18n = store ? store.i18n : null;
    if (i18n) {
        return i18n.t(key);
    }
    return key;
}

// when htmlField is empty, its value is <p><br></p>, but we want it to be an empty string so it does not do bad things to email templates, also it is necessary to fix non https links because links without https made in react-rte do not work
export function fixHtml(value) {
    if (value === '<p><br></p>') {
        return '';
    } else if  (value === '\n') {
        return '';
    } else {
        return value.replace('"www', '"https://www');
    }
}

export function isEnumSet(enumerationValue, flag) {
    return (enumerationValue & flag) === flag;
}

export function calculateColor(number) {
    if (number) {
        if (number <= 24) return palette.vibrant.red;
        if (number <= 49) return palette.vibrant.orange;
        if (number <= 74) return palette.vibrant.gold;
        if (number <= 89) return palette.evergreen.lime;
        return palette.evergreen.aspargus;
    } else {
        return palette.vibrant.red;
    }
}

// joHn dOE -> John Doe
export function titlify(string, lowerRest = false) {
    if (!string ||  typeof string !== 'string' || string.trim().length === 0) return string;
    return string
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + (lowerRest ? word.slice(1).toLowerCase() : word.slice(1)))
        .join(' ');
}

// joHn dOE -> John doe
export function capitalizeFirst(string, lowerRest = false) {
    if (!string || typeof string !== 'string' || string.trim().length === 0) return string;
    return string.charAt(0).toUpperCase() + (lowerRest ? string.slice(1).toLowerCase() : string.slice(1));
}


export function calculateScoreTitle(percentage) {
    if (percentage <= 24) return 'Very risky';
    if (percentage <= 49) return 'Risky';
    if (percentage <= 74) return 'Fair';
    if (percentage <= 89) return 'Very good';
    return 'Excellent';
}

export function isNameUntitled(name, itemString) {
    if (!name) return true;

    const defaultNames = {
        training: 'Untitled Training',
        assessment: 'Untitled Assessment',
        assessment_insight: 'Untitled Insight',
    };

    return name === defaultNames[itemString];
}


export function stripStringOfHtml(string) {
    const regex =
        /<\/?[a-z]+(?=[\s>]*)(?:[^>=]|=(?:'[^']*'|"[^"]*"|[^'"\s]*))*\s?\/?>/gis;

    return string.replace(regex, '');
}

export function substituteWithComponent(string, key, replacement) {
    if (!string.includes(key)) return string;
    return (
        <>
            {string.substring(0, string.indexOf(key))}
            {replacement}
            {string.substr(string.indexOf(key) + key.length)}
        </>
    );
}

export function isSubscriptionPlanFree(listOfPlans, subscriptionPlan) {
    const plan = listOfPlans.find((plan) => plan.code === subscriptionPlan);
    return subscriptionPlan && plan && plan.isFree;
}

export function joinPath(...parts) {
    if (!parts) return '';
    let result = '';
    for (let i = 0; i < parts.length; i++) {
        let part = parts[i];
        if (part == null) continue;
        part = part.toString();
        result += part.startsWith('/') && i > 0 ? part.substr(1) : part;

        if (i !== parts.length - 1 && !result.endsWith('/')) result += '/';
    }
    return result;
}

export function resourcePath(path) {
    if (!path) return path;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    if (path.startsWith('/static')) {
        return joinPath(
            import.meta.env.REACT_APP_STATIC_PREFIX,
            path.replace('/static', '')
        );
    }
    return path;
}

export function isArray(value) {
    return Array.isArray(value) || isObservableArray(value);
}

export function replaceSpaceWithUnderscore(string) {
    return string.replace(' ', '_');
}

export function replaceUnderscoreWithSpace(string) {
    return string.replace('_', ' ');
}

export function removeStringDiacritics(string) {
    return string
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

export function getDeliveryFrequency(value) {
    if (value === 'W') return 'week';
    if (value === 'M') return 'month';
    if (value === 'B') return 'second week';
}

export function getReminderDelayType(type, value) {
    let result;
    if (type === REMINDER_DELAY_TYPE.DAY) result = 'day';
    if (type === REMINDER_DELAY_TYPE.HOUR) result = 'hour';
    if (type === REMINDER_DELAY_TYPE.MINUTE) result = 'minute';
    return pluralize(result, value);
}

export const createDecisionFeedbackTable = (
    decisions,
    decisionLog,
    table,
    index
) => {
    if (decisions.length === index) {
        return table;
    } else {
        let decision = decisions[index];
        if (!decision.questionText) {
            return createDecisionFeedbackTable(
                decisions,
                decisionLog,
                table,
                ++index
            );
        } else {
            if (decisionLog[0]) {
                decision.answer = decisionLog[0].decision;
                decisionLog.shift();
                return createDecisionFeedbackTable(
                    decisions,
                    decisionLog,
                    [...table, decision],
                    ++index
                );
            } else {
                decision.answer = 2;
                return createDecisionFeedbackTable(
                    decisions,
                    decisionLog,
                    [...table, decision],
                    ++index
                );
            }
        }
    }
};


export function expandParticipant(email) {
    if (!email) return '';

    if (email.includes('@')) {
        const parts = email.split('|');
        if(parts.length === 1) {
            return email;
        }
        let validEmail = parts.find((part) => {
            if(validateEmail(part)) {
                return part;
            }
        });
        if (validEmail) {
            return validEmail;
        }
        return email;
    }

    const parts = email.split('|');
    if (parts.length === 2 && parts[0] === 'noemail') {
        return parts[1];
    }

    return email;
}

export function expandUserName(name) {
    if (name && name.indexOf('|') > -1) {
        let parts = name.split('|');
        if (!validateUUID(parts[0])) return parts[0];
        if (parts.length > 1) {
            return parts[1];
        } else return parts[0];
    }
    return name;
}

export function expandUserEmail(name, email) {
    if (email && email.indexOf('@') > -1) return email;

    if (name && name.indexOf('|') > -1) {
        let parts = name.split('|');
        for (let part of parts) {
            if (part.indexOf('@') > -1) {
                return part;
            }
        }
    }
    return email;
}

export function formatCurrency(amount, currency = 'USD') {
    if (!amount) {
        amount = 0;
    }
    if (!currency) {
        currency = 'USD';
    }

    return currencyFormatter.format(amount, { code: currency });
}

export function parseJson(value) {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (e) {
        return {};
    }
}

export function diffValues(oldValues, newValues) {
    const oldObj = parseJson(oldValues);
    const newObj = parseJson(newValues);
    const keys = Array.from(
        new Set([...Object.keys(oldObj), ...Object.keys(newObj)])
    );
    return keys.map((key) => {
        let oldVal = oldObj[key];
        let newVal = newObj[key];
        let status = 'unchanged';
        if (oldVal === undefined) {
            status = 'added'
            oldVal = 'N/A';
        }
        else if (newVal === undefined){
            status = 'deleted';
            newVal = 'N/A';
        }
        else if (oldVal !== newVal) status = 'changed';
        return { key, oldVal, newVal, status };
    });
}

export const NON_SSO_COPY_LINK_STORAGE_KEY = 'non-sso-copy-link';

export function isNonSsoCopyLinkEnabled() {
    const value = window.localStorage.getItem(NON_SSO_COPY_LINK_STORAGE_KEY);
    return value === '1' || value === 'true';
}

export const RESET_TRAINING_STATUS_STORAGE_KEY = 'enable-reset-training-status';

export function isResetTrainingStatusEnabled() {
    const value = window.localStorage.getItem(
        RESET_TRAINING_STATUS_STORAGE_KEY
    );
    return value === '1' || value === 'true';
}

export function getSigninLinkQueryParams(email) {
    const params = { email };
    if (isNonSsoCopyLinkEnabled()) {
        params['non-sso'] = '1';
    }
    return params;
}
